import { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { Transformer } from 'markmap-lib';
import { Markmap } from 'markmap-view';
import { X, Download, ZoomIn, ZoomOut, Minimize2, Image, FileText, FileBox } from 'lucide-react';
import { buildXmindBlobFromMarkmapRoot, exportSvgToPngBlob, type MarkmapNode } from '../utils/mindmapExport';
import { ToolbarButton } from './ToolbarButton';

interface MindMapViewerProps {
  markdown: string;
  className?: string;
}

type FullscreenToolbarProps = {
  onExportPNG: () => void | Promise<void>;
  onExportMarkdown: () => void;
  onExportXmind: () => void | Promise<void>;
  onZoomOut: () => void;
  onZoomIn: () => void;
  onResetSize: () => void;
};

function FullscreenToolbar(props: FullscreenToolbarProps) {
  const { onExportPNG, onExportMarkdown, onExportXmind, onZoomOut, onZoomIn, onResetSize } = props;
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExportMenu]);

  return (
    <div className="absolute top-4 right-4 flex items-center gap-1 bg-white dark:bg-zinc-800 rounded-lg shadow-lg border border-slate-200 dark:border-zinc-700 p-1 z-[10000]">
      {/* Export dropdown */}
      <div className="relative" ref={exportMenuRef}>
        <ToolbarButton onClick={() => setShowExportMenu(prev => !prev)} title="Export">
          <Download className="h-4 w-4" />
        </ToolbarButton>
        {showExportMenu && (
          <div className="absolute top-full right-0 mt-2 bg-white dark:bg-zinc-800 rounded-lg shadow-lg border border-slate-200 dark:border-zinc-700 py-1 min-w-[160px] dropdown-animate">
            <button
              onClick={() => {
                setShowExportMenu(false);
                void onExportPNG();
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 dark:text-zinc-300 hover:bg-slate-50 dark:hover:bg-zinc-700 transition-colors"
            >
              <Image className="h-4 w-4" />
              Image File (.png)
            </button>
            <button
              onClick={() => {
                setShowExportMenu(false);
                onExportMarkdown();
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 dark:text-zinc-300 hover:bg-slate-50 dark:hover:bg-zinc-700 transition-colors"
            >
              <FileText className="h-4 w-4" />
              Markdown (.md)
            </button>
            <button
              onClick={() => {
                setShowExportMenu(false);
                void onExportXmind();
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 dark:text-zinc-300 hover:bg-slate-50 dark:hover:bg-zinc-700 transition-colors"
            >
              <FileBox className="h-4 w-4" />
              Xmind File (.xmind)
            </button>
          </div>
        )}
      </div>

      <div className="w-px h-5 bg-slate-200 dark:bg-zinc-600" />

      {/* Zoom controls */}
      <ToolbarButton onClick={onZoomOut} title="Zoom Out">
        <ZoomOut className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton onClick={onZoomIn} title="Zoom In">
        <ZoomIn className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton onClick={onResetSize} title="Reset Size">
        <Minimize2 className="h-4 w-4" />
      </ToolbarButton>
    </div>
  );
}

export interface MindMapViewerRef {
  zoomIn: () => void;
  zoomOut: () => void;
  resetSize: () => void;
  toggleFullscreen: () => void;
  exportPNG: () => Promise<void>;
  exportMarkdown: () => void;
  exportXmind: () => Promise<void>;
  isLoading: boolean;
  isFullscreen: boolean;
}

// Lazy-loaded transformer singleton to avoid re-initialization
let transformerInstance: Transformer | null = null;
const getTransformer = () => {
  if (!transformerInstance) {
    transformerInstance = new Transformer();
  }
  return transformerInstance;
};

export const MindMapViewer = forwardRef<MindMapViewerRef, MindMapViewerProps>(
  ({ markdown, className = '' }, ref) => {
    const svgRef = useRef<SVGSVGElement | null>(null);
    const fullscreenSvgRef = useRef<SVGSVGElement | null>(null);
    const markmapRef = useRef<Markmap | null>(null);
    const fullscreenMarkmapRef = useRef<Markmap | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const renderIdRef = useRef(0);
    const previousBodyOverflowRef = useRef<string | null>(null);

    useEffect(() => {
      setIsLoading(true);
      const renderId = ++renderIdRef.current;
      const svgEl = svgRef.current;
      if (!svgEl) return;

      const transformer = getTransformer();
      const { root } = transformer.transform(markdown);

      if (!markmapRef.current) {
        markmapRef.current = Markmap.create(svgEl, undefined, root);
      } else {
        markmapRef.current.setData(root);
      }

      void Promise.resolve(markmapRef.current.fit())
        .catch(() => undefined)
        .finally(() => {
          if (renderIdRef.current === renderId) {
            setIsLoading(false);
          }
        });
    }, [markdown]);

    // Initialize fullscreen mindmap when entering fullscreen
    useEffect(() => {
      if (!isFullscreen) return;
      const svgEl = fullscreenSvgRef.current;
      if (!svgEl) return;

      const transformer = getTransformer();
      const { root } = transformer.transform(markdown);

      if (!fullscreenMarkmapRef.current) {
        fullscreenMarkmapRef.current = Markmap.create(svgEl, undefined, root);
      } else {
        fullscreenMarkmapRef.current.setData(root);
      }

      void Promise.resolve(fullscreenMarkmapRef.current.fit()).catch(() => undefined);
    }, [isFullscreen, markdown]);

    useEffect(() => {
      if (isFullscreen) return;
      if (!fullscreenMarkmapRef.current) return;
      fullscreenMarkmapRef.current.destroy();
      fullscreenMarkmapRef.current = null;
    }, [isFullscreen]);

    useEffect(() => {
      if (!isFullscreen) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setIsFullscreen(false);
        }
      };

      previousBodyOverflowRef.current = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleKeyDown);

      const handleResize = () => {
        void fullscreenMarkmapRef.current?.fit();
      };
      window.addEventListener('resize', handleResize);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('resize', handleResize);
        document.body.style.overflow = previousBodyOverflowRef.current ?? '';
        previousBodyOverflowRef.current = null;
      };
    }, [isFullscreen]);

    useEffect(() => {
      return () => {
        markmapRef.current?.destroy();
        fullscreenMarkmapRef.current?.destroy();
        markmapRef.current = null;
        fullscreenMarkmapRef.current = null;
      };
    }, []);

    useEffect(() => {
      if (typeof ResizeObserver === 'undefined') return;
      const svgEl = svgRef.current;
      if (!svgEl) return;

      let rafId: number | null = null;
      const observer = new ResizeObserver(() => {
        if (rafId != null) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          void markmapRef.current?.fit();
        });
      });

      observer.observe(svgEl);
      return () => {
        observer.disconnect();
        if (rafId != null) cancelAnimationFrame(rafId);
      };
    }, []);

    const handleZoomIn = useCallback(() => {
      if (isFullscreen && fullscreenMarkmapRef.current) {
        fullscreenMarkmapRef.current.rescale(1.25);
      } else if (markmapRef.current) {
        markmapRef.current.rescale(1.25);
      }
    }, [isFullscreen]);

    const handleZoomOut = useCallback(() => {
      if (isFullscreen && fullscreenMarkmapRef.current) {
        fullscreenMarkmapRef.current.rescale(0.8);
      } else if (markmapRef.current) {
        markmapRef.current.rescale(0.8);
      }
    }, [isFullscreen]);

    const handleResetSize = useCallback(() => {
      if (isFullscreen && fullscreenMarkmapRef.current) {
        void fullscreenMarkmapRef.current.fit();
      } else if (markmapRef.current) {
        void markmapRef.current.fit();
      }
    }, [isFullscreen]);

    const handleToggleFullscreen = useCallback(() => {
      setIsFullscreen(prev => !prev);
    }, []);

    const handleExportPNG = useCallback(async () => {
      const targetSvg = isFullscreen ? fullscreenSvgRef.current : svgRef.current;
      if (!targetSvg) return;

      try {
        const blob = await exportSvgToPngBlob(targetSvg, { scale: 2, backgroundColor: '#ffffff' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'mindmap.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Failed to export PNG:', error);
      }
    }, [isFullscreen]);

    const handleExportMarkdown = useCallback(() => {
      const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
      const link = document.createElement('a');
      link.download = 'mindmap.md';
      link.href = URL.createObjectURL(blob);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }, [markdown]);

    const handleExportXmind = useCallback(async () => {
      try {
        const transformer = getTransformer();
        const { root } = transformer.transform(markdown);
        const blob = await buildXmindBlobFromMarkmapRoot(root as unknown as MarkmapNode);
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = 'mindmap.xmind';
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Failed to export XMind:', error);
      }
    }, [markdown]);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      zoomIn: handleZoomIn,
      zoomOut: handleZoomOut,
      resetSize: handleResetSize,
      toggleFullscreen: handleToggleFullscreen,
      exportPNG: handleExportPNG,
      exportMarkdown: handleExportMarkdown,
      exportXmind: handleExportXmind,
      isLoading,
      isFullscreen,
    }), [handleZoomIn, handleZoomOut, handleResetSize, handleToggleFullscreen, handleExportPNG, handleExportMarkdown, handleExportXmind, isLoading, isFullscreen]);

    // Fullscreen overlay rendered via Portal to document.body
    const fullscreenOverlay = isFullscreen ? createPortal(
      <div
        className="fixed inset-0 bg-white dark:bg-zinc-900 z-[9999]"
        style={{ margin: 0, padding: 0 }}
      >
        {/* Exit fullscreen button */}
        <button
          onClick={() => setIsFullscreen(false)}
          className="absolute top-4 left-4 p-2 bg-white dark:bg-zinc-800 rounded-lg shadow-lg border border-slate-200 dark:border-zinc-700 hover:bg-slate-50 dark:hover:bg-zinc-700 transition-colors z-[10000]"
          title="Exit Fullscreen"
        >
          <X className="h-5 w-5 text-slate-600 dark:text-zinc-300" />
        </button>

        {/* Fullscreen toolbar */}
        <FullscreenToolbar
          onExportPNG={handleExportPNG}
          onExportMarkdown={handleExportMarkdown}
          onExportXmind={handleExportXmind}
          onZoomOut={handleZoomOut}
          onZoomIn={handleZoomIn}
          onResetSize={handleResetSize}
        />

        {/* Fullscreen Mind map SVG */}
        <svg
          ref={fullscreenSvgRef}
          className="h-full w-full"
        />
      </div>,
      document.body
    ) : null;

    return (
      <>
        <div className={`relative h-full w-full bg-white dark:bg-zinc-900 ${className}`}>
          {/* Loading overlay */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-zinc-900/80 z-10">
              <div className="h-8 w-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Mind map SVG */}
          <svg
            ref={svgRef}
            className={`h-full w-full transition-opacity duration-200 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
          />
        </div>

        {/* Fullscreen overlay via Portal */}
        {fullscreenOverlay}
      </>
    );
  }
);

MindMapViewer.displayName = 'MindMapViewer';
